import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Save, Calendar, Tag, History, Download, Archive, 
  CheckCircle, Clock, RefreshCw, Settings, FileText,
  Building2, Globe, Award, ExternalLink, Upload, Plus,
  Trash2, Sparkles, Bug, Wrench, Shield
} from 'lucide-react';
import api, { settingsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const CHANGELOG_TYPES = [
  { value: 'feature', label: 'Nouvelle fonctionnalité', icon: Sparkles, color: 'bg-green-100 text-green-700' },
  { value: 'fix', label: 'Correction', icon: Bug, color: 'bg-red-100 text-red-700' },
  { value: 'improvement', label: 'Amélioration', icon: Wrench, color: 'bg-blue-100 text-blue-700' },
  { value: 'security', label: 'Sécurité', icon: Shield, color: 'bg-purple-100 text-purple-700' }
];

const EspaceAgence = () => {
  const [settings, setSettings] = useState({});
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [versionNote, setVersionNote] = useState('');
  const [qualiopiUrl, setQualiopiUrl] = useState('');
  const [showChangelogForm, setShowChangelogForm] = useState(false);
  const [changelogForm, setChangelogForm] = useState({
    title: '',
    description: '',
    type: 'feature',
    version: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, changelogRes] = await Promise.all([
        settingsAPI.get(),
        api.get('/settings/changelog')
      ]);
      setSettings(settingsRes.data);
      setQualiopiUrl(settingsRes.data.qualiopi_certificate_url || '');
      setChangelog(changelogRes.data);
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de charger les données', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await settingsAPI.get();
      setSettings(res.data);
      setQualiopiUrl(res.data.qualiopi_certificate_url || '');
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de charger les paramètres', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Mettre à jour le certificat Qualiopi
  const updateQualiopiCertificate = async () => {
    setSaving(true);
    try {
      await settingsAPI.update({ qualiopi_certificate_url: qualiopiUrl });
      setSettings({ ...settings, qualiopi_certificate_url: qualiopiUrl });
      toast({ 
        title: 'Certificat mis à jour', 
        description: 'Le lien vers le certificat Qualiopi a été mis à jour.'
      });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de mettre à jour le certificat', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Fonction pour formater la date du jour en français
  const formatTodayDate = () => {
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return new Date().toLocaleDateString('fr-FR', options);
  };

  // Mettre à jour la date de dernière mise à jour
  const updateLastUpdateDate = async () => {
    setSaving(true);
    try {
      const todayFormatted = formatTodayDate();
      await settingsAPI.update({ last_update_date: todayFormatted });
      setSettings({ ...settings, last_update_date: todayFormatted });
      toast({ title: 'Date mise à jour', description: `La date a été mise à jour : ${todayFormatted}` });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de mettre à jour la date', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Ajouter une entrée au changelog
  const addChangelogEntry = async (e) => {
    e.preventDefault();
    if (!changelogForm.title.trim()) {
      toast({ title: 'Titre requis', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/settings/changelog', changelogForm);
      setChangelog([res.data, ...changelog]);
      setChangelogForm({ title: '', description: '', type: 'feature', version: '' });
      setShowChangelogForm(false);
      toast({ title: 'Entrée ajoutée', description: 'Le changelog a été mis à jour' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Supprimer une entrée du changelog
  const deleteChangelogEntry = async (entryId) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    try {
      await api.delete(`/settings/changelog/${entryId}`);
      setChangelog(changelog.filter(c => c.id !== entryId));
      toast({ title: 'Entrée supprimée' });
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const getTypeInfo = (type) => {
    return CHANGELOG_TYPES.find(t => t.value === type) || CHANGELOG_TYPES[0];
  };

  // Créer une nouvelle version avec backup
  const createNewVersion = async () => {
    if (!versionNote.trim()) {
      toast({ title: 'Note requise', description: 'Veuillez ajouter une note pour cette version', variant: 'destructive' });
      return;
    }

    setCreatingVersion(true);
    try {
      // Incrémenter la version
      const currentVersion = settings.app_version || "1.0.0";
      const versionParts = currentVersion.split('.');
      const newPatch = parseInt(versionParts[2] || 0) + 1;
      const newVersion = `${versionParts[0]}.${versionParts[1]}.${newPatch}`;

      // Créer l'entrée dans l'historique
      const versionEntry = {
        version: newVersion,
        date: new Date().toISOString(),
        date_formatted: formatTodayDate(),
        note: versionNote,
        created_by: 'Agence MSC'
      };

      const versionHistory = settings.version_history || [];
      versionHistory.unshift(versionEntry); // Ajouter au début

      // Mettre à jour les settings
      await settingsAPI.update({
        app_version: newVersion,
        last_update_date: formatTodayDate(),
        version_history: versionHistory
      });

      setSettings({
        ...settings,
        app_version: newVersion,
        last_update_date: formatTodayDate(),
        version_history: versionHistory
      });

      setVersionNote('');
      toast({ 
        title: 'Version enregistrée', 
        description: `Version ${newVersion} créée avec succès. Backup effectué.` 
      });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de créer la version', variant: 'destructive' });
    } finally {
      setCreatingVersion(false);
    }
  };

  // Incrémenter manuellement la version majeure ou mineure
  const incrementVersion = async (type) => {
    setSaving(true);
    try {
      const currentVersion = settings.app_version || "1.0.0";
      const versionParts = currentVersion.split('.').map(Number);
      
      let newVersion;
      if (type === 'major') {
        newVersion = `${versionParts[0] + 1}.0.0`;
      } else if (type === 'minor') {
        newVersion = `${versionParts[0]}.${versionParts[1] + 1}.0`;
      }

      await settingsAPI.update({ app_version: newVersion });
      setSettings({ ...settings, app_version: newVersion });
      toast({ title: 'Version mise à jour', description: `Nouvelle version : ${newVersion}` });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de mettre à jour la version', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Espace Agence">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-[#285BBB]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Espace Agence MSC" 
      subtitle="Gestion des versions et mises à jour de l'application"
    >
      <div className="space-y-6">
        {/* État actuel */}
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-[#285BBB] to-[#1e4a9a] rounded-xl p-6 text-white"
          >
            <div className="flex items-center gap-3 mb-4">
              <Tag className="h-6 w-6" />
              <h3 className="text-lg font-semibold">Version actuelle</h3>
            </div>
            <p className="text-4xl font-bold mb-2">{settings.app_version || "1.0.0"}</p>
            <p className="text-blue-200 text-sm">
              Dernière mise à jour : {settings.last_update_date || "Non définie"}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="h-6 w-6 text-[#285BBB]" />
              <h3 className="text-lg font-semibold text-[#2C3C65]">Mettre à jour la date</h3>
            </div>
            <p className="text-[#6777A1] text-sm mb-4">
              Cliquez pour mettre à jour la date de dernière mise à jour avec la date du jour.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex-1 bg-gray-100 rounded-lg px-4 py-3">
                <p className="text-sm text-gray-500">Date du jour</p>
                <p className="font-semibold text-[#2C3C65]">{formatTodayDate()}</p>
              </div>
              <Button 
                onClick={updateLastUpdateDate} 
                disabled={saving}
                className="bg-[#285BBB] hover:bg-[#1e4a9a]"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Appliquer
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Certificat Qualiopi */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-xl shadow-md p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <Award className="h-6 w-6 text-[#ff3a2d]" />
            <h3 className="text-lg font-semibold text-[#2C3C65]">Certificat Qualiopi</h3>
          </div>
          <p className="text-[#6777A1] text-sm mb-4">
            Mettez à jour le lien vers le certificat Qualiopi. Ce certificat sera accessible depuis le logo Qualiopi dans le footer du site.
            <br />
            <span className="text-amber-600 font-medium">À renouveler chaque année.</span>
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                URL du certificat Qualiopi (PDF)
              </label>
              <Input
                value={qualiopiUrl}
                onChange={(e) => setQualiopiUrl(e.target.value)}
                placeholder="https://exemple.com/certificat-qualiopi-2025.pdf"
                className="h-12"
              />
            </div>
            
            <div className="flex items-center gap-4">
              {settings.qualiopi_certificate_url && (
                <a 
                  href={settings.qualiopi_certificate_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[#285BBB] hover:underline text-sm"
                >
                  <ExternalLink className="h-4 w-4" />
                  Voir le certificat actuel
                </a>
              )}
              <div className="flex-1" />
              <Button 
                onClick={updateQualiopiCertificate}
                disabled={saving || !qualiopiUrl}
                className="bg-[#ff3a2d] hover:bg-[#e6352a]"
              >
                <Save className="h-4 w-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </div>
          
          {/* Aperçu */}
          {qualiopiUrl && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-2">Aperçu du lien :</p>
              <div className="flex items-center gap-2">
                <img 
                  src="/qualiopi-logo.png"
                  alt="Qualiopi"
                  className="h-10"
                />
                <span className="text-sm text-[#285BBB] truncate">{qualiopiUrl}</span>
              </div>
            </div>
          )}
        </motion.div>

        {/* Créer une nouvelle version avec backup */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-md p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <Archive className="h-6 w-6 text-green-600" />
            <h3 className="text-lg font-semibold text-[#2C3C65]">Enregistrer une nouvelle version</h3>
          </div>
          <p className="text-[#6777A1] text-sm mb-6">
            Créez un point de sauvegarde avec un numéro de version incrémenté.
            La date de mise à jour sera automatiquement mise à jour.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                Note de version (changelog)
              </label>
              <Textarea
                value={versionNote}
                onChange={(e) => setVersionNote(e.target.value)}
                placeholder="Ex: Ajout de la gestion des certifications, correction du formulaire d'inscription..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1 bg-blue-50 rounded-lg px-4 py-3">
                <p className="text-sm text-blue-600">Prochaine version</p>
                <p className="font-bold text-[#2C3C65] text-xl">
                  {(() => {
                    const v = (settings.app_version || "1.0.0").split('.');
                    return `${v[0]}.${v[1]}.${parseInt(v[2] || 0) + 1}`;
                  })()}
                </p>
              </div>
              <Button 
                onClick={createNewVersion}
                disabled={creatingVersion || !versionNote.trim()}
                className="bg-green-600 hover:bg-green-700 px-6"
              >
                {creatingVersion ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Créer version & Backup
              </Button>
            </div>
          </div>

          {/* Options avancées de versioning */}
          <div className="mt-6 pt-6 border-t">
            <p className="text-sm font-medium text-[#2C3C65] mb-3">Incrémentation manuelle</p>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => incrementVersion('minor')}
                disabled={saving}
              >
                Version mineure ({(() => {
                  const v = (settings.app_version || "1.0.0").split('.');
                  return `${v[0]}.${parseInt(v[1]) + 1}.0`;
                })()})
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => incrementVersion('major')}
                disabled={saving}
              >
                Version majeure ({(() => {
                  const v = (settings.app_version || "1.0.0").split('.');
                  return `${parseInt(v[0]) + 1}.0.0`;
                })()})
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Historique des versions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-md p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <History className="h-6 w-6 text-[#285BBB]" />
            <h3 className="text-lg font-semibold text-[#2C3C65]">Historique des versions</h3>
          </div>

          {(settings.version_history && settings.version_history.length > 0) ? (
            <div className="space-y-3">
              {settings.version_history.map((entry, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-4 p-4 rounded-lg ${index === 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}
                >
                  <div className={`px-3 py-1 rounded-full text-sm font-bold ${index === 0 ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-700'}`}>
                    v{entry.version}
                  </div>
                  <div className="flex-1">
                    <p className="text-[#2C3C65] font-medium">{entry.note}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {entry.date_formatted} • {entry.created_by}
                    </p>
                  </div>
                  {index === 0 && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                      Actuelle
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Aucun historique de version disponible</p>
              <p className="text-sm">Créez votre première version pour commencer l'historique.</p>
            </div>
          )}
        </motion.div>

        {/* Changelog - Journal des modifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white rounded-xl shadow-md p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileText className="h-6 w-6 text-[#285BBB]" />
              <h3 className="text-lg font-semibold text-[#2C3C65]">Journal des modifications</h3>
            </div>
            <Button
              onClick={() => setShowChangelogForm(!showChangelogForm)}
              className="bg-[#285BBB] hover:bg-[#2C3C65]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter une entrée
            </Button>
          </div>

          {/* Formulaire d'ajout */}
          <AnimatePresence>
            {showChangelogForm && (
              <motion.form
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                onSubmit={addChangelogEntry}
                className="mb-6 p-4 bg-gray-50 rounded-lg overflow-hidden"
              >
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">Titre *</label>
                    <Input
                      value={changelogForm.title}
                      onChange={(e) => setChangelogForm({ ...changelogForm, title: e.target.value })}
                      placeholder="Ex: Ajout de la gestion des groupes"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2C3C65] mb-1">Type</label>
                      <select
                        value={changelogForm.type}
                        onChange={(e) => setChangelogForm({ ...changelogForm, type: e.target.value })}
                        className="w-full rounded-md border border-gray-300 p-2"
                      >
                        {CHANGELOG_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2C3C65] mb-1">Version</label>
                      <Input
                        value={changelogForm.version}
                        onChange={(e) => setChangelogForm({ ...changelogForm, version: e.target.value })}
                        placeholder="Ex: 1.2.0"
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#2C3C65] mb-1">Description</label>
                  <Textarea
                    value={changelogForm.description}
                    onChange={(e) => setChangelogForm({ ...changelogForm, description: e.target.value })}
                    placeholder="Description détaillée des modifications..."
                    rows={2}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowChangelogForm(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={saving} className="bg-[#285BBB]">
                    {saving ? 'Ajout...' : 'Ajouter'}
                  </Button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Liste des entrées */}
          {changelog.length > 0 ? (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {changelog.map((entry) => {
                const typeInfo = getTypeInfo(entry.type);
                const TypeIcon = typeInfo.icon;
                return (
                  <div key={entry.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg group">
                    <div className={`p-2 rounded-lg ${typeInfo.color}`}>
                      <TypeIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-[#2C3C65]">{entry.title}</p>
                        {entry.version && (
                          <span className="px-2 py-0.5 bg-[#285BBB] text-white text-xs rounded">
                            v{entry.version}
                          </span>
                        )}
                      </div>
                      {entry.description && (
                        <p className="text-sm text-[#6777A1] mt-1">{entry.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(entry.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        {' • '}{entry.author}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteChangelogEntry(entry.id)}
                      className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Aucune entrée dans le changelog</p>
              <p className="text-sm">Ajoutez votre première entrée pour documenter les modifications.</p>
            </div>
          )}
        </motion.div>

        {/* Informations de l'agence */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gray-50 rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="h-6 w-6 text-[#285BBB]" />
            <h3 className="text-lg font-semibold text-[#2C3C65]">Agence MSC</h3>
          </div>
          <p className="text-[#6777A1] text-sm">
            Cette application a été développée par l'Agence MSC. 
            Pour toute demande de modification ou d'évolution, contactez votre référent.
          </p>
          <div className="mt-4 flex gap-4">
            <a 
              href="https://agence-msc.fr" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#285BBB] hover:underline text-sm flex items-center gap-1"
            >
              <Globe className="h-4 w-4" />
              www.agence-msc.fr
            </a>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default EspaceAgence;
